// YouTubeEmbed — thin wrapper around the YouTube IFrame Player API, shared
// by the verse-clip playing card (VerseClip.dc.html) and the new sermon
// sheet's pinned player (SermonSheet.dc.html) — docs/FIX-SERMON-CLIPS.md §2/§3.
//
// Config mirrors public/yt-test.html exactly (confirmed working in the
// Median app): playsinline, rel=0, enablejsapi, origin, and a
// strict-origin-when-cross-origin referrer on the generated iframe.
//
// This is a pure embed primitive — it does NOT decide what happens on
// error 101/150 (embedding disabled for that video). Callers get the
// YouTube error code via onError and decide whether to swap to a
// link-out fallback; that decision differs between the verse sheet's
// collapsed row and the sermon sheet's always-on player.
//
// Props:
//   videoId        — YouTube video id
//   start           — seconds to start at (0 = beginning)
//   end             — seconds to stop at; omit to let it play through to
//                      the end of the video (the sermon sheet's full player)
//   autoplay        — default true (the verse sheet's design: tapping a row
//                      is the single tap that starts playback)
//   onReady(player) — native YT.Player instance, for advanced control
//   onEnded()       — fired when playback reaches `end` (or the video's own
//                      end, if no `end` was given) — YouTube's own ended state
//   onError(code)   — YouTube error code (2, 5, 100, 101, 150 ...)
//   onTimeUpdate(s) — polled every 500ms while playing (the IFrame API has
//                      no native timeupdate event)
//   onPlayingChange(isPlaying) — fired on every YouTube state change, true
//                      exactly when PlayerState.PLAYING. Callers should use
//                      this (not a locally-guessed boolean) as the source of
//                      truth for a "Playing"/"Paused" UI, since control calls
//                      made through the imperative handle below are async.

import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";

let apiPromise = null;
function loadYouTubeApi() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve) => {
    const prevReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { prevReady?.(); resolve(window.YT); };
    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    }
  });
  return apiPromise;
}

const YouTubeEmbed = forwardRef(function YouTubeEmbed(
  { videoId, start = 0, end, autoplay = true, onReady, onEnded, onError, onTimeUpdate, onPlayingChange },
  ref
) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const pollRef = useRef(null);
  const [, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi().then(YT => {
      if (cancelled || !YT || !containerRef.current || !videoId) return;
      playerRef.current = new YT.Player(containerRef.current, {
        videoId,
        playerVars: {
          start: Math.max(0, Math.floor(start) || 0),
          ...(end ? { end: Math.floor(end) } : {}),
          playsinline: 1, rel: 0, enablejsapi: 1,
          origin: window.location.origin,
        },
        events: {
          onReady: () => {
            if (cancelled) return;
            setReady(true);
            if (autoplay) playerRef.current.playVideo();
            onReady?.(playerRef.current);
          },
          onStateChange: (e) => {
            if (!YT) return;
            onPlayingChange?.(e.data === YT.PlayerState.PLAYING);
            if (e.data === YT.PlayerState.ENDED) onEnded?.();
            if (e.data === YT.PlayerState.PLAYING) {
              if (pollRef.current) clearInterval(pollRef.current);
              pollRef.current = setInterval(() => {
                try { onTimeUpdate?.(playerRef.current.getCurrentTime()); } catch {}
              }, 500);
            } else if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
          },
          onError: (e) => onError?.(e.data),
        },
      });
    });
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      try { playerRef.current?.destroy(); } catch {}
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  useImperativeHandle(ref, () => ({
    play:  () => { try { playerRef.current?.playVideo(); } catch {} },
    pause: () => { try { playerRef.current?.pauseVideo(); } catch {} },
    seekTo: (s) => { try { playerRef.current?.seekTo(s, true); } catch {} },
    getCurrentTime: () => { try { return playerRef.current?.getCurrentTime() || 0; } catch { return 0; } },
  }), []);

  return <div ref={containerRef} style={{ width: "100%", height: "100%" }} referrerPolicy="strict-origin-when-cross-origin"/>;
});

export default YouTubeEmbed;

// Seconds → "m:ss" / "h:mm:ss", shared by every clip/player UI.
export function fmtClock(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

// Clip bounds per docs/FIX-SERMON-CLIPS.md §2 "Clip rules" — moment/
// cross_reference get a short window around the timestamp; main_passage
// gets a longer one from the timestamp itself. No timestamp → whole sermon.
export function clipBoundsFor(ref) {
  // Callers pass two different shapes: raw sermon_refs rows (snake_case
  // timestamp_s — ReadingPlanDaySheet's watchRef) and BibleApp.jsx's own
  // normalized clip objects (camelCase timestampS — fetchVerseClips()).
  // Reading only timestamp_s meant every verse-clip card fell through to
  // "no timestamp" and always started at 0, regardless of the real moment.
  const ts = ref?.timestamp_s ?? ref?.timestampS;
  if (ts == null) return { start: 0, end: null, isFullSermon: true };
  if (ref.kind === "main_passage") return { start: ts, end: ts + 180, isFullSermon: false };
  return { start: Math.max(0, ts - 3), end: Math.max(0, ts - 3) + 90, isFullSermon: false };
}
